import { getMdfActivityContributions } from 'partner360/features/mdfActivities/lib'
import {
  IMdfActivityWithBusinessPriorities,
  TMdfBreakdownGroupField,
} from 'partner360/features/mdfActivities/model/types'

import { EMPTY_GROUP_LABEL } from './getCockpitInitiativeGroups'
import type { ICockpitMdfGroup, ICockpitMdfRow } from './types'

const uniqueValues = (values: string[]): string[] => Array.from(new Set(values))

const toRow = (
  activity: IMdfActivityWithBusinessPriorities,
  requested: number,
  paid: number
): ICockpitMdfRow => ({
  activityId: activity.activityId,
  name: activity.activityName || EMPTY_GROUP_LABEL,
  activityType: activity.activityType || EMPTY_GROUP_LABEL,
  timeframe: activity.timeframe || EMPTY_GROUP_LABEL,
  status: activity.activityStatus || EMPTY_GROUP_LABEL,
  // The legacy lists repeat a value once per allocation row (`";Print;Print;Print"`), so they
  // are deduplicated for display — the same rule the MDF table and details modal apply.
  strategicInitiatives: uniqueValues(activity.strategicInitiatives),
  businessUnit: uniqueValues(activity.businessUnit),
  category: uniqueValues(
    activity.strategicInitiativeAllocations.map(({ category }) => category).filter(Boolean)
  ),
  businessPriorities: uniqueValues(activity.businessPriorities),
  requested,
  paid,
})

/**
 * Buckets the in-scope MDF activities by the selected group field, keeping the activities that
 * fed each bucket alongside its totals — the chart reads the totals, the master-detail list
 * reads the rows, and both stay consistent because they come from one pass.
 *
 * Which buckets an activity lands in, and with how much money, is
 * `getMdfActivityContributions` — the same primitive the MDF dashboard's breakdown chart uses,
 * so the category-split rules (allocation split, apportioned paid, FX rate, zero-money
 * activities dropped) are not restated here. A row therefore carries this bucket's share of the
 * activity rather than the activity's total.
 */
export const getCockpitMdfGroups = (
  activities: IMdfActivityWithBusinessPriorities[],
  groupField: TMdfBreakdownGroupField,
  isUsd: boolean
): ICockpitMdfGroup[] => {
  const groups = new Map<string, ICockpitMdfGroup>()

  activities.forEach((activity) => {
    const activityContributions = new Map<string, { requested: number; paid: number }>()

    getMdfActivityContributions(activity, groupField, isUsd).forEach(
      ({ name, requested, paid }) => {
        const contribution = activityContributions.get(name) ?? { requested: 0, paid: 0 }

        activityContributions.set(name, {
          requested: contribution.requested + requested,
          paid: contribution.paid + paid,
        })
      }
    )

    activityContributions.forEach(({ requested, paid }, name) => {
      const group = groups.get(name) ?? { name, rows: [], requested: 0, paid: 0 }

      groups.set(name, {
        name,
        rows: [...group.rows, toRow(activity, requested, paid)],
        requested: group.requested + requested,
        paid: group.paid + paid,
      })
    })
  })

  return Array.from(groups.values()).sort((left, right) => right.requested - left.requested)
}
